import ErrorDialog from "@/components/ErrorDialog"
import UsernameMark from "@/components/UsernameMark"
import { useAuth } from "@/hooks/useAuth"
import { useAuthModal } from "@/hooks/useAuthModal"
import { lumi } from "@/lib/lumi"
import { APP_NAME, APP_TAGLINE } from "@/lib/brand"
import { publicProfilesFor } from "@/lib/profile"
import { privacyPublicProfiles } from "@/lib/privacy"
import { unblockAccount } from "@/lib/social"
import type { Block, Profile } from "@/types"
import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"

export default function SettingsPage() {
  const { user, isAuthenticated, signOut } = useAuth()
  const { openAuth } = useAuthModal()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [blocks, setBlocks] = useState<Block[]>([])
  const [blockedProfiles, setBlockedProfiles] = useState<Profile[]>([])
  const [notifyEnabled, setNotifyEnabled] = useState(() => localStorage.getItem("reelhood_notify_enabled") !== "0")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const myId = user?.userId || ""

  useEffect(() => {
    if (!isAuthenticated || !myId) {
      openAuth()
      setLoading(false)
      return
    }
    const load = async () => {
      setLoading(true)
      try {
        const [profileRes, blockRes] = await Promise.all([
          lumi.entities.profiles.list({ filter: { userId: myId }, limit: 1 }),
          lumi.entities.blocks.list({ filter: { blockerId: myId }, sort: { createdAt: -1 }, limit: 200 }),
        ])
        const matched = ((profileRes?.list || []) as Profile[]).find((item) => item.userId === myId) || null
        const myBlocks = ((blockRes?.list || []) as Block[]).filter((item) => item.blockerId === myId)
        const blockedIds = myBlocks.map((item) => item.blockedId).filter(Boolean)
        const blockedPeople = blockedIds.length ? await privacyPublicProfiles({ userIds: blockedIds }) : []
        setProfile(matched)
        setBlocks(myBlocks)
        setBlockedProfiles(publicProfilesFor(blockedPeople, blockedIds))
      } catch (err) {
        console.error("Settings load failed:", err)
        setError("Could not load settings.")
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isAuthenticated, myId, openAuth])

  const setPrivacy = async (privacy: "public" | "followers") => {
    if (!profile?._id) return
    try {
      await lumi.entities.profiles.update(profile._id, { privacy, updatedAt: new Date().toISOString() })
      setProfile((prev) => (prev ? { ...prev, privacy } : prev))
    } catch (err) {
      console.error("Privacy update failed:", err)
      setError("Could not update privacy.")
    }
  }

  const toggleNotify = () => {
    const next = !notifyEnabled
    setNotifyEnabled(next)
    localStorage.setItem("reelhood_notify_enabled", next ? "1" : "0")
  }

  const unblock = async (blockedId: string) => {
    try {
      await unblockAccount(myId, blockedId)
      setBlocks((prev) => prev.filter((item) => item.blockedId !== blockedId))
      setBlockedProfiles((prev) => prev.filter((item) => item.userId !== blockedId))
    } catch (err) {
      console.error("Unblock failed:", err)
      setError("Could not unblock that account.")
    }
  }

  const handleLogout = () => {
    signOut()
    navigate("/")
    openAuth()
  }

  if (!isAuthenticated || !myId) {
    return <p className="px-4 py-10 text-center text-sm text-[#6E6E6E]">Sign in to open settings.</p>
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 lg:px-8">
      <h1 className="font-[Syne] text-2xl font-bold">Settings</h1>
      <p className="mt-1 text-sm text-[#A8A8A8]">Notifications, privacy, and account controls.</p>
      <Link to="/profile?tab=prefs" className="mt-4 inline-flex text-sm font-semibold text-[#00C805]">Edit feed preferences</Link>

      <section className="mt-6 rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-4">
        <h2 className="text-sm font-semibold">Notifications</h2>
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-[#A8A8A8]">Push and in-app alerts</p>
          <button
            type="button"
            onClick={toggleNotify}
            aria-pressed={notifyEnabled}
            className={`h-6 w-11 rounded-full transition ${notifyEnabled ? "bg-[#00FF66]" : "bg-[#1F1F1F]"}`}
          >
            <span
              className={`block h-5 w-5 translate-y-0.5 rounded-full bg-black transition ${
                notifyEnabled ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
      </section>

      <section className="mt-4 rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-4">
        <h2 className="text-sm font-semibold">Privacy</h2>
        <p className="mt-1 text-xs text-[#6E6E6E]">Who can see your posts.</p>
        <div className="mt-3 grid grid-cols-2 gap-1 rounded-full bg-[#141414] p-1">
          {(["public", "followers"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setPrivacy(option)}
              className={`rounded-full py-2 text-xs font-semibold capitalize ${
                (profile?.privacy || "public") === option ? "bg-[#00FF66] text-black" : "text-[#A8A8A8]"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </section>

      <section className="mt-4 rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-4">
        <h2 className="text-sm font-semibold">Blocked accounts</h2>
        {loading ? (
          <p className="mt-3 text-sm text-[#6E6E6E]">Loading...</p>
        ) : blocks.length === 0 ? (
          <p className="mt-3 text-sm text-[#6E6E6E]">You haven&apos;t blocked anyone.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {blocks.map((block) => {
              const target = blockedProfiles.find((item) => item.userId === block.blockedId)
              return (
                <div key={block._id} className="flex items-center justify-between gap-3">
                  <Link to={`/u/${block.blockedId}`} className="min-w-0 text-sm">
                    <UsernameMark username={target?.username} userId={block.blockedId} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => unblock(block.blockedId)}
                    className="shrink-0 rounded-full border border-[#1F1F1F] px-3 py-1 text-xs text-white"
                  >
                    Unblock
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section className="mt-4 rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] p-4">
        <h2 className="text-sm font-semibold">About</h2>
        <p className="mt-2 text-sm text-[#A8A8A8]">{APP_NAME} — {APP_TAGLINE}</p>
        <p className="mt-2 text-xs text-[#6E6E6E]">Built with Lumi.</p>
      </section>

      <button
        type="button"
        onClick={handleLogout}
        className="mt-6 w-full rounded-xl border border-[#1F1F1F] py-3 text-sm font-semibold text-white"
      >
        Log out
      </button>

      {error ? <ErrorDialog message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}
